import React from "react";
import {
  Easing,
  Img,
  interpolate,
  interpolateColors,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { useCopy } from "../copy";
import { LOOK } from "../look";
import { useStage } from "../stage";
import { SCENE_BEATS, useTiming } from "../timing";
import { Footnote, Headline, Split, Sub } from "../ui/Split";
import { Sfx } from "../ui/Sfx";
import { Card, usePop } from "../ui/Surfaces";
import { Jar } from "../ui/Jar";

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;

/**
 * Scene 2 — the hidden asset, then the blind spot, on one beat each half.
 *
 *   A  "Vos clients vous recommandent déjà." A private chat: a friend asks,
 *      your customer sends the link (the B2C hook, seen from the brand).
 *   B  (SWITCH) "Mais vous ne le voyez pas." The same link lands in your
 *      analytics as "Direct", the biggest line nobody can name (37 % on
 *      average, Marjan et al. 2020, from the CTO's dark-social post).
 */

/**
 * The switch lands on the scene's 5th beat (see `useTiming`): B carries
 * three times the words of A (headline, sub, source), so it gets the
 * longer half.
 */
const SWITCH_BEAT = 5;
/**
 * The ambassador's reply lands in the chat. Chat pacing is in frames, not
 * beats (a conversation's rhythm, not the music's); it only has to land
 * well before the switch.
 */
const REPLY = 28;
/** B's sub and source follow the headline closely: reading time first. */
const SUB_AFTER = 10;
const FOOTNOTE_AFTER = 26;
/** The analytics card starts in while the chat leaves: no empty frame. */
const CARD_LEAD = 5;
const VISUAL = { w: 820, h: 640 };

// ---------------------------------------------------------------------------
// A — the chat
// ---------------------------------------------------------------------------

const Bubble: React.FC<{
  out?: boolean;
  at: number;
  children: React.ReactNode;
}> = ({ out = false, at, children }) => {
  const p = usePop(at);
  return (
    <div
      style={{
        alignSelf: out ? "flex-end" : "flex-start",
        maxWidth: 560,
        padding: "20px 28px",
        borderRadius: 36,
        borderBottomRightRadius: out ? 10 : 36,
        borderBottomLeftRadius: out ? 36 : 10,
        background: out ? LOOK.blue : LOOK.mist,
        color: out ? LOOK.white : LOOK.ink,
        fontFamily: LOOK.body,
        fontWeight: LOOK.medium,
        fontSize: 36,
        lineHeight: 1.2,
        transformOrigin: out ? "100% 100%" : "0% 100%",
        scale: at <= 0 ? 1 : p,
        opacity: at <= 0 ? 1 : Math.min(1, p * 2),
      }}
    >
      {children}
    </div>
  );
};

const Chat: React.FC = () => {
  const frame = useCurrentFrame();
  const copy = useCopy();
  return (
    <Card
      style={{
        width: VISUAL.w,
        height: VISUAL.h,
        padding: "28px 36px",
        display: "flex",
        flexDirection: "column",
        gap: 16,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 18,
          paddingBottom: 20,
          marginBottom: 8,
          borderBottom: `2px solid ${LOOK.mist}`,
        }}
      >
        <Img
          src={staticFile("images/scene1/4.jpg")}
          style={{
            width: 68,
            height: 68,
            borderRadius: 68,
            objectFit: "cover",
          }}
        />
        <div>
          <div
            style={{
              fontFamily: LOOK.display,
              fontWeight: LOOK.bold,
              fontSize: 34,
              color: LOOK.ink,
            }}
          >
            {copy.dark.contact}
          </div>
          <div
            style={{ fontFamily: LOOK.body, fontSize: 24, color: LOOK.green }}
          >
            ● {copy.dark.status}
          </div>
        </div>
        {/* WhatsApp-style glyph: this is a private channel. */}
        <svg
          style={{ marginLeft: "auto" }}
          width="46"
          height="46"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M12 2.5a9.5 9.5 0 0 0-8.2 14.3L2.5 21.5l4.8-1.3A9.5 9.5 0 1 0 12 2.5Z"
            stroke={LOOK.green}
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="M8.6 7.9c.2-.4.5-.4.8-.4h.5c.2 0 .4 0 .5.4l.7 1.7c.1.2 0 .4-.1.6l-.5.6c-.1.1-.2.3 0 .5.6 1 1.4 1.8 2.5 2.4.2.1.4.1.5-.1l.6-.7c.2-.2.4-.2.6-.1l1.6.8c.3.1.3.3.3.5 0 .5-.2 1.2-.8 1.5-.6.4-1.5.5-2.6.1-1.9-.7-3.7-2.4-4.6-4.2-.6-1.2-.4-2.3 0-3.1Z"
            fill={LOOK.green}
          />
        </svg>
      </div>
      <Bubble at={0}>{copy.dark.incoming1}</Bubble>
      <Bubble at={12}>{copy.dark.incoming2}</Bubble>
      {frame >= REPLY ? (
        <Bubble out at={REPLY}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 18,
              padding: 12,
              margin: "-4px -12px 14px",
              borderRadius: 24,
              background: "rgba(255,255,255,0.14)",
            }}
          >
            <Jar size={96} background={LOOK.white} />
            <div style={{ lineHeight: 1.2 }}>
              <div style={{ fontWeight: LOOK.semi, fontSize: 32 }}>
                {copy.dark.linkTitle}
              </div>
              <div style={{ fontSize: 26, opacity: 0.75 }}>
                {copy.dark.domain}
              </div>
            </div>
          </div>
          {copy.dark.reply} 👇
        </Bubble>
      ) : null}
    </Card>
  );
};

// ---------------------------------------------------------------------------
// B — the analytics blind spot
// ---------------------------------------------------------------------------

/** `t` = frames since the switch (the card's own clock). */
const Analytics: React.FC<{ t: number }> = ({ t }) => {
  const { fps } = useVideoConfig();
  const copy = useCopy();
  const max = copy.dark.rows[0][1];
  const note = spring({
    frame: t - 38,
    fps,
    config: { damping: 20, stiffness: 200, mass: 0.6 },
  });
  // "Direct" row lights up once the bars have grown.
  const hot = interpolate(t, [30, 38], [0, 1], clamp);
  return (
    <Card
      style={{
        width: VISUAL.w,
        height: VISUAL.h,
        padding: "44px 48px",
        overflow: "visible",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 34,
        }}
      >
        <div
          style={{
            fontFamily: LOOK.display,
            fontWeight: LOOK.bold,
            fontSize: 40,
            color: LOOK.ink,
          }}
        >
          {copy.dark.analyticsTitle}
        </div>
        <div style={{ fontFamily: LOOK.body, fontSize: 24, color: LOOK.muted }}>
          Analytics
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 26 }}>
        {copy.dark.rows.map(([label, share], i) => {
          const grow = spring({
            frame: t - 4 - i * 3,
            fps,
            config: { damping: 20, stiffness: 140, mass: 0.8 },
          });
          const direct = i === 0;
          const barColor = direct
            ? interpolateColors(hot, [0, 1], [LOOK.blueSoft, LOOK.red])
            : LOOK.blueSoft;
          return (
            <div
              key={label}
              style={{
                display: "grid",
                gridTemplateColumns: "250px 1fr 90px",
                alignItems: "center",
                gap: 20,
                padding: direct ? "14px 18px" : "0 18px",
                margin: direct ? "-14px 0" : 0,
                borderRadius: 22,
                background: direct
                  ? interpolateColors(
                      hot,
                      [0, 1],
                      ["rgba(0,0,0,0)", "rgba(229, 20, 55, 0.08)"],
                    )
                  : undefined,
              }}
            >
              <div
                style={{
                  fontFamily: LOOK.body,
                  fontWeight: direct ? LOOK.bold : LOOK.medium,
                  fontSize: 32,
                  color: direct ? LOOK.ink : LOOK.inkSoft,
                }}
              >
                {label}
                {/* What "Direct" actually is. */}
                {direct ? (
                  <div
                    style={{
                      height: 34 * note,
                      marginTop: 4 * note,
                      overflow: "hidden",
                      fontWeight: LOOK.semi,
                      fontSize: 21,
                      color: LOOK.red,
                      whiteSpace: "nowrap",
                      opacity: note,
                    }}
                  >
                    = {copy.dark.directNote}
                  </div>
                ) : null}
              </div>
              <div
                style={{ height: 26, borderRadius: 26, background: LOOK.mist }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${(share / max) * 100 * grow}%`,
                    borderRadius: 26,
                    background: barColor,
                  }}
                />
              </div>
              <div
                style={{
                  textAlign: "right",
                  fontFamily: LOOK.display,
                  fontWeight: LOOK.bold,
                  fontSize: 32,
                  color: direct ? LOOK.red : LOOK.ink,
                  fontVariantNumeric: "tabular-nums",
                  opacity: grow,
                }}
              >
                {Math.round(share * grow)}
                {copy.pct}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export const Dark: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const timing = useTiming();
  // Frames from the scene start to its 6th beat, on the music's grid.
  const SWITCH = timing.at(SCENE_BEATS.hook + SWITCH_BEAT) - timing.start.dark;
  const copy = useCopy();
  const stage = useStage();
  const isB = frame >= SWITCH;
  const t = frame - SWITCH;

  // Visual swap on the beat: the chat leaves left just before the
  // switch while the analytics card springs in from the right, so the
  // frame is never empty (the card's bars still wait for the switch).
  const out = interpolate(frame, [SWITCH - 7, SWITCH], [0, 1], {
    ...clamp,
    easing: Easing.bezier(0.7, 0, 0.84, 0),
  });
  const cardIn = frame >= SWITCH - CARD_LEAD;
  const inP = spring({
    frame: t + CARD_LEAD,
    fps,
    config: { damping: 18, stiffness: 190, mass: 0.7 },
  });

  return (
    <>
      <Split
        background={isB ? LOOK.white : LOOK.sky}
        visualSize={VISUAL}
        textH={stage.shape === "tall" ? 380 : 300}
        // The source runs on two lines under the card.
        bottomRoom={stage.shape === "tall" ? 150 : 120}
        text={
          isB ? (
            <>
              <Headline text={copy.dark.headlineB} start={SWITCH} />
              <Sub text={copy.dark.sub} at={SWITCH + SUB_AFTER} />
            </>
          ) : (
            <Headline text={copy.dark.headlineA} start={2} />
          )
        }
        visual={
          <div
            style={{ position: "relative", width: VISUAL.w, height: VISUAL.h }}
          >
            {!isB ? (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  translate: `${-out * 160}px 0`,
                  opacity: 1 - out,
                }}
              >
                <Chat />
              </div>
            ) : null}
            {cardIn ? (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  translate: `${(1 - inP) * 200}px 0`,
                  opacity: Math.min(1, inP * 1.6),
                }}
              >
                <Analytics t={t} />
              </div>
            ) : null}
          </div>
        }
      >
        {isB ? (
          <Footnote text={copy.dark.footnote} at={SWITCH + FOOTNOTE_AFTER} />
        ) : null}
      </Split>
      <Sfx sound="bubble-in" at={0} />
      <Sfx sound="bubble-in" at={12} />
      <Sfx sound="bubble-out" at={REPLY} />
      <Sfx sound="slide" at={SWITCH} />
      {/* "Direct" turns red (Analytics: hot 30→38), then the note pops. */}
      <Sfx sound="negative" at={SWITCH + 32} />
      <Sfx sound="pop" at={SWITCH + 38} volume={0.7} />
    </>
  );
};
